/* RESPONSIBLE TEAM: team-ai-agent */

import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import { type Router } from '@ember/routing';
import { inject as service } from '@ember/service';
import { ReactRoute } from 'embercom/lib/react-route';
import { hash } from 'rsvp';
import { taskFor } from 'ember-concurrency-ts';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import type Transition from '@ember/routing/transition';
import type Store from '@ember-data/store';

class FinAiAgentSalesPlaybookRoute extends Route {
  @service declare appService: AppService;
  @service declare router: Router;
  @service declare store: Store;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;

  private hasUnsavedChanges = false;
  private unsubscribeDirtyState?: () => void;

  reactEnabled() {
    return true;
  }

  async beforeModel(transition: Transition) {
    this.permissionsService.ensurePermissionWhenTransitioning(
      'can_create_and_edit_bots',
      transition,
    );

    if (!this.appService.app.canUseFinSalesAgent) {
      return this.router.transitionTo('apps.app.automation.fin-ai-agent.setup');
    }
    await taskFor(this.reportingMetrics.setup).perform();
    await this.store.findAll('messenger-app');
    return hash({});
  }

  activate() {
    this.unsubscribeDirtyState = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-sdr:dirty-state-changed',
      (data) => {
        let { isDirty } = data as { isDirty: boolean };
        this.hasUnsavedChanges = isDirty;
      },
    );
  }

  deactivate() {
    this.unsubscribeDirtyState?.();
    this.hasUnsavedChanges = false;
  }

  @action
  async willTransition(transition: Transition) {
    if (this.hasUnsavedChanges) {
      transition.abort();

      let confirmOptions = {
        title: this.intl.t('ai-agent.sales-development.unsaved-changes-modal.title'),
        confirmButtonText: this.intl.t('ai-agent.sales-development.unsaved-changes-modal.confirm'),
        cancelButtonText: this.intl.t('ai-agent.sales-development.unsaved-changes-modal.cancel'),
        body: this.intl.t('ai-agent.sales-development.unsaved-changes-modal.body'),
        primaryButtonType: 'primary-destructive' as const,
      };

      let isConfirmed = await this.intercomConfirmService.confirm(confirmOptions);
      if (!isConfirmed) {
        return;
      }

      this.hasUnsavedChanges = false;
      transition.retry();
    }
  }
}
export default ReactRoute(FinAiAgentSalesPlaybookRoute, {
  overrideRenderTemplate: false,
});
